// src/index.js
import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import "./index.css";
import ErrorBoundary from "./components/ErrorBoundary";
import * as serviceWorkerRegistration from "./serviceWorkerRegistration";

// Vercel Web Analytics
import { Analytics } from "@vercel/analytics/react";

const RESIZE_OBSERVER_LOOP_ERROR_PATTERNS = [
  "resizeobserver loop completed with undelivered notifications",
  "resizeobserver loop limit exceeded",
];
const MAIN_BUNDLE_RE = /\/static\/js\/main\.([a-z0-9]+)\.js/i;
const UPDATE_CHECK_INTERVAL_MS = 5 * 60 * 1000;
const ICON_REFRESH_HINT_KEY = "nia_icon_refresh_hint";
let manifestBlobUrl = "";

function getCurrentBundleHash() {
  const scripts = Array.from(document.querySelectorAll("script[src]"));
  const mainScript = scripts.find((node) => MAIN_BUNDLE_RE.test(node.src || ""));
  if (!mainScript) return "";
  const match = String(mainScript.src).match(MAIN_BUNDLE_RE);
  return match?.[1] || "";
}

function extractBundleHashFromHtml(html = "") {
  const match = String(html).match(MAIN_BUNDLE_RE);
  return match?.[1] || "";
}

function withVersionParam(url, version) {
  if (!url || !version) return url;
  try {
    const next = new URL(url, window.location.origin);
    next.searchParams.set("v", version);
    if (next.origin === window.location.origin) {
      return `${next.pathname}${next.search}${next.hash}`;
    }
    return next.toString();
  } catch {
    return url;
  }
}

async function applyInstallAssetVersion(version) {
  if (!version || typeof document === "undefined") return;

  try {
    const iconLinks = document.querySelectorAll('link[rel="icon"], link[rel="apple-touch-icon"]');
    iconLinks.forEach((node) => {
      const href = node.getAttribute("href") || "";
      if (!href) return;
      node.setAttribute("href", withVersionParam(href, version));
    });

    const manifestLink = document.querySelector('link[rel="manifest"]');
    if (!manifestLink) return;
    const manifestHref = manifestLink.getAttribute("href") || "/manifest.json";
    const versionedManifestHref = withVersionParam(manifestHref, version);
    manifestLink.setAttribute("href", versionedManifestHref);

    // Build a runtime manifest URL whose icon URLs also include the same version.
    const response = await fetch(versionedManifestHref, { cache: "no-store" });
    if (!response.ok) return;
    const manifest = await response.json();
    if (Array.isArray(manifest.icons)) {
      manifest.icons = manifest.icons.map((icon) => ({
        ...icon,
        src: withVersionParam(icon.src, version),
      }));
    }
    const blob = new Blob([JSON.stringify(manifest)], { type: "application/manifest+json" });
    if (manifestBlobUrl) URL.revokeObjectURL(manifestBlobUrl);
    manifestBlobUrl = URL.createObjectURL(blob);
    manifestLink.setAttribute("href", manifestBlobUrl);
  } catch {
    // Best effort only.
  }
}

function hardReloadForUpdate(nextHash) {
  const sessionKey = "nia_last_forced_reload_hash";
  const alreadyReloaded = sessionStorage.getItem(sessionKey);
  if (alreadyReloaded === nextHash) return;
  try {
    localStorage.setItem(ICON_REFRESH_HINT_KEY, "1");
  } catch {}
  sessionStorage.setItem(sessionKey, nextHash);
  const nextUrl = new URL(window.location.href);
  nextUrl.searchParams.set("nia_update", Date.now().toString());
  window.location.replace(nextUrl.toString());
}

async function checkForAppUpdate() {
  try {
    const currentHash = getCurrentBundleHash();
    if (!currentHash) return;

    const response = await fetch("/index.html", {
      cache: "no-store",
      headers: { "cache-control": "no-cache" },
    });
    if (!response.ok) return;

    const html = await response.text();
    const latestHash = extractBundleHashFromHtml(html);
    if (!latestHash || latestHash === currentHash) return;

    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
    }
    hardReloadForUpdate(latestHash);
  } catch {
    // Silent fallback: update checks should never block app usage.
  }
}

if (process.env.NODE_ENV === "development" && typeof window !== "undefined") {
  const isResizeObserverLoopError = (value) => {
    const message = String(
      value?.message ||
      value?.reason?.message ||
      value?.error?.message ||
      value ||
      ""
    ).toLowerCase();
    return RESIZE_OBSERVER_LOOP_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
  };

  const originalConsoleError = console.error;
  console.error = (...args) => {
    if (args.some((arg) => isResizeObserverLoopError(arg))) return;
    originalConsoleError(...args);
  };

  const swallowResizeObserverOverlay = (event) => {
    if (!isResizeObserverLoopError(event)) return;
    event.preventDefault?.();
    event.stopImmediatePropagation?.();
  };

  window.addEventListener("error", swallowResizeObserverOverlay);
  window.addEventListener("unhandledrejection", swallowResizeObserverOverlay);
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
    <Analytics />
  </React.StrictMode>
);

serviceWorkerRegistration.register();

if (process.env.NODE_ENV === "production" && typeof window !== "undefined") {
  const currentHash = getCurrentBundleHash();
  if (currentHash) applyInstallAssetVersion(currentHash);

  window.setTimeout(() => {
    checkForAppUpdate();
  }, 4000);

  window.setInterval(() => {
    checkForAppUpdate();
  }, UPDATE_CHECK_INTERVAL_MS);

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") checkForAppUpdate();
  });
}
